<template>
  <Transition name="tb-action-overlay-fade">
    <div
      v-if="overlayStore.isVisible"
      class="fixed inset-0 z-[10050] flex flex-col items-center justify-center bg-gray-900/80 backdrop-blur-md"
    >
      <div class="relative flex flex-col items-center">
        <!-- SUCCESS (PCG ActionOverlay) -->
        <div v-if="overlayStore.mode === 'success'" class="icon-container mb-8">
          <svg class="h-28 w-28 text-emerald-400" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <circle
              class="success-circle"
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="currentColor"
              stroke-width="6"
            />
            <path
              class="success-check"
              d="M30 50 L45 65 L70 35"
              fill="none"
              stroke="currentColor"
              stroke-width="6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <!-- DELETE (PCG ActionOverlay) -->
        <div v-else-if="overlayStore.mode === 'delete'" class="icon-container mb-8">
          <svg class="h-28 w-28 text-red-500" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M30 40 L35 85 C35 90 65 90 65 85 L70 40 Z"
              fill="none"
              stroke="currentColor"
              stroke-width="6"
              stroke-linejoin="round"
            />
            <line x1="42" y1="50" x2="44" y2="75" stroke="currentColor" stroke-width="4" stroke-linecap="round" />
            <line x1="58" y1="50" x2="56" y2="75" stroke="currentColor" stroke-width="4" stroke-linecap="round" />
            <rect class="delete-item" x="40" y="-10" width="20" height="15" rx="2" fill="currentColor" />
            <g class="delete-lid">
              <line x1="20" y1="35" x2="80" y2="35" stroke="currentColor" stroke-width="6" stroke-linecap="round" />
              <path
                d="M40 35 L45 25 L55 25 L60 35"
                fill="none"
                stroke="currentColor"
                stroke-width="6"
                stroke-linejoin="round"
              />
            </g>
          </svg>
        </div>

        <!-- SYNC (PCG ActionOverlay) -->
        <div v-else-if="overlayStore.mode === 'sync'" class="icon-container mb-8">
          <svg class="sync-spin h-28 w-28 text-blue-400" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M 50 20 A 30 30 0 0 1 80 50"
              fill="none"
              stroke="currentColor"
              stroke-width="8"
              stroke-linecap="round"
            />
            <polygon points="80,50 70,35 90,35" fill="currentColor" />
            <path
              d="M 50 80 A 30 30 0 0 1 20 50"
              fill="none"
              stroke="currentColor"
              stroke-width="8"
              stroke-linecap="round"
            />
            <polygon points="20,50 10,65 30,65" fill="currentColor" />
          </svg>
        </div>

        <h2 class="mb-3 text-center text-2xl font-bold tracking-wide text-white sm:text-3xl">
          {{ overlayStore.title }}
        </h2>
        <p class="flex items-center gap-3 text-center text-lg text-gray-300">
          <svg
            v-if="overlayStore.mode === 'sync'"
            class="h-5 w-5 animate-spin text-blue-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          {{ overlayStore.message }}
        </p>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { useActionOverlayStore } from '@/stores/action-overlay'

const overlayStore = useActionOverlayStore()
</script>

<style scoped>
.tb-action-overlay-fade-enter-active,
.tb-action-overlay-fade-leave-active {
  transition: opacity 0.4s ease;
}

.tb-action-overlay-fade-enter-from,
.tb-action-overlay-fade-leave-to {
  opacity: 0;
}

.success-circle {
  stroke-dasharray: 260;
  stroke-dashoffset: 260;
  animation: draw-circle 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}

.success-check {
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  animation: draw-check 0.4s cubic-bezier(0.65, 0, 0.45, 1) 0.5s forwards;
}

@keyframes draw-circle {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes draw-check {
  to {
    stroke-dashoffset: 0;
  }
}

.delete-lid {
  transform-origin: 80px 35px;
  animation: lid-open-close 1.2s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
}

.delete-item {
  opacity: 0;
  animation: item-drop 1.2s ease-in forwards;
}

@keyframes lid-open-close {
  0% {
    transform: rotate(0deg);
  }
  20% {
    transform: rotate(45deg);
  }
  60% {
    transform: rotate(45deg);
  }
  80% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

@keyframes item-drop {
  0% {
    transform: translateY(0) scale(1);
    opacity: 0;
  }
  20% {
    transform: translateY(10px) scale(1);
    opacity: 1;
  }
  50% {
    transform: translateY(60px) scale(0.5);
    opacity: 1;
  }
  60% {
    transform: translateY(60px) scale(0);
    opacity: 0;
  }
  100% {
    transform: translateY(60px) scale(0);
    opacity: 0;
  }
}

.sync-spin {
  animation: spin-pulse 1.5s ease-in-out infinite;
  transform-origin: center;
}

@keyframes spin-pulse {
  0% {
    transform: rotate(0deg) scale(1);
  }
  50% {
    transform: rotate(180deg) scale(1.1);
  }
  100% {
    transform: rotate(360deg) scale(1);
  }
}
</style>
